import * as React from 'react';

import Dinero from 'dinero.js';

import { connect } from 'react-redux';

import AppState from '@webapp/store/state';

import styled from 'styled-components';

import { Translate } from 'react-localize-redux';

import { EarningsData } from '@webapp/types/ui/earnings/earnings';

import StreamListTotalCtrl from '../earnings-item-summary/stream-list';

import Typography from '@components/basics/typography';
import EarningsItemSummaryCtrl from '../earnings-item-summary';
import CollapsableBox from '@webapp/controllers/collapsable-box';

import { getStreamingServiceLogo } from '@webapp/utils/get-streaming-service-logo';

interface EarningsListCtrlProps {
  className?: string;
  earnings: EarningsData[];
  isPro?: boolean;
  hidePaymentDates?: boolean;
}

const mapStateToProps = (state: AppState) => ({
  isPro: state.subscription.current?.plan?.includes('Pro'),
});

const getStreamingTotal = (month: EarningsData, key: string) => {
  const total = month.platforms[key]?.reduce(
    (memo, item) => memo.add(item.revenue),
    Dinero({ amount: 0, currency: 'USD', precision: 5 })
  );

  return total?.toFormat() || '$0';
};

const getPlatformKey = (dsp: string): string => {
  switch (dsp) {
    case 'Amazon Unlimited':
      return 'amazonMusic';
    case 'Amazon':
      return 'amazonStore';
    case 'Apple Music':
      return 'appleMusic';
    case 'iTunes':
      return 'itunes';
    case 'Spotify':
      return 'spotify';
    case 'Tidal':
      return 'tidal';
    case 'Youtube Ad Supported':
      return 'youtube';
    case 'Youtube Music':
      return 'youtube';
    case 'Google Play':
      return 'googleStore';
    case 'Deezer':
      return 'deezer';
    case 'Pandora':
      return 'pandora';
    case 'Soundcloud':
      return 'soundcloud';
    default:
      return '';
  }
};

const getPlatformTitle = (platform: string): JSX.Element => {
  const platformKey = getPlatformKey(platform);

  return (
    <>
      {platformKey ? (
        getStreamingServiceLogo(platformKey)
      ) : (
        <Typography type="title">{platform}</Typography>
      )}
    </>
  );
};

export const EarningsListCtrl: React.FC<EarningsListCtrlProps> = (props) => {
  return (
    <>
      {props.earnings.map((month) => (
        <CollapsableBox
          className={props.className}
          key={month.date.toISOString()}
          title={month.date.format('MMMM, YYYY')}
          customContent={
            <>
              <Typography type="title">
                <Translate id="USER_PROFILE.WALLET.EARNINGS.PAYOUT" />:{' '}
                {month.total?.toFormat() || 0}
              </Typography>
              <Typography type="title" weight="400" className="mb-10">
                <Translate id="USER_PROFILE.WALLET.EARNINGS.TEXT.1" />{' '}
                {month.streams?.toLocaleString() || 0}{' '}
                <Translate id="USER_PROFILE.WALLET.EARNINGS.TEXT.2" />{' '}
                {month.downloads?.toLocaleString() || 0}{' '}
                <Translate id="USER_PROFILE.WALLET.EARNINGS.TEXT.3" />
              </Typography>
              {!props.hidePaymentDates ? (
                month.paid ? (
                  <Typography type="title" weight="400">
                    <Translate
                      id={
                        month.partialPayment
                          ? 'USER_PROFILE.WALLET.EARNINGS.PAID.PARTIAL'
                          : 'USER_PROFILE.WALLET.EARNINGS.PAID'
                      }
                    />{' '}
                    {month.paid?.format('LL') || 0}
                  </Typography>
                ) : (
                  <Typography type="title" weight="400">
                    <Translate id="USER_PROFILE.WALLET.EARNINGS.AVAILABLE" />{' '}
                    {month.eligible?.format('LL') || 0}
                  </Typography>
                )
              ) : null}
            </>
          }
        >
          <EarningsItemSummaryCtrl
            title={<Translate id="USER_PROFILE.WALLET.EARNINGS.TITLE" />}
            titleTotal={<Translate id="USER_PROFILE.WALLET.EARNINGS.PAYOUT" />}
            total={month.total?.toFormat()}
            items={month.distribution}
            isPro={props.isPro}
          />
          <StreamListTotalCtrl
            className="mt-40"
            isPro={props.isPro}
            data={month.categories}
          />
          {Object.keys(month.platforms).map((key: string) => (
            <EarningsItemSummaryCtrl
              key={key}
              className="mb-80"
              title={getPlatformTitle(key)}
              titleTotal={
                <Translate id="USER_PROFILE.WALLET.EARNINGS.PAYOUT" />
              }
              total={getStreamingTotal(month, key)}
              items={month.platforms[key] || []}
              isPro={props.isPro}
            />
          ))}
        </CollapsableBox>
      ))}
    </>
  );
};

export default connect(mapStateToProps)(styled(EarningsListCtrl)`
  margin-bottom: 10px;

  &:last-child {
    margin-bottom: 0;
  }
`);
